import { useCallback, useMemo, useState } from "react";

import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { ulid } from "ulid";

import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { Alert, Button, Stack } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import { formatNumber } from "@/features/shared";
import { useSnackbar } from "@/features/ui";
import { supportsExpenses } from "../../purchase-return-rules";
import { SelectPurchaseInvoiceExpenseDialog } from "./select-purchase-invoice-expense-dialog";

/**
 * Expenses charged back by a return. The backend only accepts them on an
 * invoice-sourced return, and only as references to that invoice's own expense
 * lines — so they are picked, never typed, and every other source shows why the
 * tab is empty rather than an editor that would be rejected on submit.
 */
export const ExpensesSection = () => {
  const { control, setValue } = useFormContext();
  const { showSnackbar } = useSnackbar();

  const [selectDialogOpen, setSelectDialogOpen] = useState(false);

  const { append, remove } = useFieldArray({
    control,
    name: "purchaseReturnExpenses",
  });

  const [
    purchaseReturnExpenses,
    returnableType,
    returnableReferenceNumber,
    buyer,
  ] = useWatch({
    control,
    name: [
      "purchaseReturnExpenses",
      "returnableType",
      "returnableReferenceNumber",
      "buyer",
    ],
  });

  // DataGrid freezes whatever array it gets as `rows`; give it a copy so the
  // form keeps ownership of its own state.
  const rows = useMemo(
    () => [...purchaseReturnExpenses],
    [purchaseReturnExpenses]
  );

  const handleDelete = useCallback(
    (id) => () => {
      const index = purchaseReturnExpenses.findIndex(
        (expense) => expense.id === id
      );

      if (index !== -1) {
        remove(index);
        showSnackbar("Biaya berhasil dihapus", "success");
      }
    },
    [purchaseReturnExpenses, remove, showSnackbar]
  );

  const handleAttachInvoiceExpenses = (selectedExpenses) => {
    selectedExpenses.forEach(
      ({
        id: purchaseInvoiceExpenseId,
        outletChartOfAccount,
        remainingAmount,
        note,
      }) => {
        const alreadyExists = purchaseReturnExpenses.some(
          (expense) =>
            expense.purchaseInvoiceExpenseId === purchaseInvoiceExpenseId
        );

        if (!alreadyExists) {
          append({
            id: ulid(),
            purchaseInvoiceExpenseId,
            outletChartOfAccount,
            remainingAmount,
            amount: remainingAmount,
            note: note || null,
          });
        }
      }
    );
  };

  const processRowUpdate = (newRow) => {
    return new Promise((resolve, reject) => {
      const amount = Number(newRow.amount);

      if (Number.isNaN(amount) || amount < 0) {
        reject(new Error("Nominal tidak boleh kurang dari 0"));
        return;
      }

      // The backend caps each line at the source expense's remaining amount.
      if (amount > newRow.remainingAmount) {
        reject(
          new Error(
            `Nominal tidak boleh melebihi sisa retur (${formatNumber(
              newRow.remainingAmount
            )})`
          )
        );
        return;
      }

      const index = purchaseReturnExpenses.findIndex(
        (expense) => expense.id === newRow.id
      );

      if (index === -1) {
        reject(new Error("Biaya tidak ditemukan"));
        return;
      }

      setValue(`purchaseReturnExpenses.${index}.amount`, amount, {
        shouldDirty: true,
      });
      resolve({ ...newRow, amount });
    });
  };

  const columns = [
    {
      field: "outletChartOfAccount",
      headerName: "Akun",
      flex: 3,
      valueGetter: (value) => (value ? `${value.code} - ${value.name}` : "-"),
    },
    {
      field: "note",
      headerName: "Catatan",
      flex: 2,
      valueGetter: (value) => value || "-",
    },
    {
      field: "remainingAmount",
      headerName: "Sisa Retur",
      type: "number",
      flex: 1,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "amount",
      headerName: "Retur",
      type: "number",
      flex: 1,
      editable: true,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Aksi",
      getActions: ({ id }) => [
        <GridActionsCellItem
          key={id}
          icon={<DeleteIcon />}
          label="Hapus"
          onClick={handleDelete(id)}
          color="error"
        />,
      ],
    },
  ];

  if (!supportsExpenses(returnableType)) {
    return (
      <Alert severity="info">
        Biaya hanya dapat diretur dari Faktur Pembelian, karena setiap biaya
        mengacu pada baris biaya faktur sumbernya.
      </Alert>
    );
  }

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={!returnableReferenceNumber}
          onClick={() => setSelectDialogOpen(true)}
        >
          Ambil Biaya Faktur
        </Button>
      </Stack>

      <DataGrid
        getRowHeight={() => "auto"}
        editMode="row"
        columns={columns}
        rows={rows}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) =>
          showSnackbar(error.message, "error")
        }
        disableRowSelectionOnClick
        hideFooter
      />

      {selectDialogOpen && (
        <SelectPurchaseInvoiceExpenseDialog
          outletId={buyer?.id}
          referenceNumber={returnableReferenceNumber}
          attachedExpenses={purchaseReturnExpenses}
          onClose={() => setSelectDialogOpen(false)}
          onSubmit={handleAttachInvoiceExpenses}
        />
      )}
    </Stack>
  );
};
